'use client';

import Link from 'next/link';
import { products, type Product } from '@/lib/products';
import { BrowserShot, PhoneShot, Tilt } from '@/components/products/shots';
import { Reveal } from '@/components/products/Reveal';
import {
  AccentGlow,
  CtaRow,
  DemoBand,
  FeatureRows,
  FloatChip,
  SectionHead,
} from '@/components/products/blocks';

const SITE_ACCENT = '#34d399';

/** Mono index + name + tagline + blurb + chips + explore link. */
function ProductCopy({ product }: { product: Product }) {
  return (
    <div className="max-w-xl">
      <span
        className="font-mono text-[11px] uppercase tracking-[0.3em]"
        style={{ color: product.accent }}
      >
        {product.index} · {product.domain}
      </span>
      <h3 className="mt-5 font-six-caps text-7xl lowercase leading-[0.88] text-white sm:text-8xl">
        {product.name}
      </h3>
      <p className="mt-5 text-xl font-light leading-snug text-white md:text-2xl">
        {product.tagline}
      </p>
      <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-white/55 md:text-base">
        {product.blurb}
      </p>
      <ul className="mt-7 flex flex-wrap gap-2.5" aria-label="Highlights">
        {product.chips.map((chip) => (
          <li
            key={chip}
            className="rounded-full border border-white/12 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60"
          >
            {chip}
          </li>
        ))}
      </ul>
      <Link
        href={`/products/${product.slug}`}
        data-cursor-label="View"
        className="group mt-9 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white"
      >
        <span
          className="border-b pb-1 transition-colors duration-300"
          style={{ borderColor: product.accent }}
        >
          Explore {product.name}
        </span>
        <span
          aria-hidden="true"
          className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-1"
          style={{ color: product.accent }}
        >
          →
        </span>
      </Link>
    </div>
  );
}

export function ProductsIndexClient() {
  const [crm, erp, menu] = products;

  return (
    <div className="self-styled relative text-white">
      {/* ── Hero: triptych of the three products ─────────────────────── */}
      <section className="relative flex min-h-[100dvh] flex-col justify-center overflow-hidden px-6 pb-20 pt-[clamp(7rem,12vw,9rem)] md:px-12 lg:px-24">
        <div className="mx-auto grid w-full max-w-[1400px] items-center gap-14 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
          <div>
            <h1
              className="pv-load font-six-caps text-[clamp(4rem,9vw,8.5rem)] lowercase leading-[0.88]"
              style={{ animationDelay: '80ms' }}
            >
              Software built for daily work
            </h1>
            <p
              className="pv-load mt-8 max-w-xl text-base font-light leading-relaxed text-white/65 md:text-lg"
              style={{ animationDelay: '220ms' }}
            >
              DilaCRM, DilaERP and dila.menu support customer service, business
              operations and hospitality teams.
            </p>
            <div className="pv-load" style={{ animationDelay: '360ms' }}>
              <CtaRow
                primary={{ label: 'Book a demo', href: '/contact' }}
                secondary={{ label: 'Meet the lineup', href: '#lineup' }}
                accent={SITE_ACCENT}
                className="mt-10"
              />
            </div>
          </div>

          {/* Desktop triptych */}
          <div className="pv-load relative hidden h-[560px] lg:block" style={{ animationDelay: '300ms' }}>
            <AccentGlow accent={SITE_ACCENT} className="left-1/3 top-1/4 h-80 w-80" opacity={0.14} />
            <div className="absolute left-0 top-24 w-[64%] -rotate-3 opacity-80">
              <BrowserShot
                src={erp.image}
                alt={erp.imageAlt}
                url={erp.domain}
                accent={erp.accent}
                aspect="aspect-[16/10]"
                sizes="40vw"
              />
            </div>
            <div className="absolute right-0 top-0 w-[70%] rotate-2">
              <Tilt>
                <BrowserShot
                  src={crm.image}
                  alt={crm.imageAlt}
                  url={crm.domain}
                  accent={crm.accent}
                  aspect="aspect-[16/10]"
                  priority
                  sizes="45vw"
                />
              </Tilt>
            </div>
            <div className="absolute -bottom-2 right-8 w-[24%] rotate-6">
              <PhoneShot src={menu.image} alt={menu.imageAlt} priority sizes="15vw" />
            </div>
          </div>

          {/* Mobile hero shot */}
          <div className="pv-load lg:hidden" style={{ animationDelay: '300ms' }}>
            <BrowserShot
              src={crm.image}
              alt={crm.imageAlt}
              url={crm.domain}
              accent={crm.accent}
              priority
            />
          </div>
        </div>
      </section>

      {/* ── 01 · DilaCRM — split world ───────────────────────────────── */}
      <section
        id="lineup"
        aria-label="DilaCRM"
        className="relative border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24"
      >
        <span
          aria-hidden="true"
          className="pv-ghost pointer-events-none absolute -top-6 right-0 select-none font-six-caps text-[20vw] leading-none"
        >
          01
        </span>
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <ProductCopy product={crm} />
          </Reveal>
          <Reveal className="relative lg:col-span-7" delay={120}>
            <AccentGlow accent={crm.accent} className="-left-10 top-1/3 h-72 w-72" />
            <div className="relative rotate-1">
              <Tilt>
                <BrowserShot
                  src={crm.image}
                  alt={crm.imageAlt}
                  url={crm.domain}
                  accent={crm.accent}
                  sizes="(max-width: 1024px) 100vw, 55vw"
                />
              </Tilt>
            </div>
            <FloatChip accent={crm.accent} className="-left-4 top-8" dot>
              WhatsApp
            </FloatChip>
            <FloatChip accent={crm.accent} className="-right-3 top-1/3" alt>
              Instagram
            </FloatChip>
            <FloatChip accent={crm.accent} className="bottom-10 left-1/4">
              TikTok · Telegram · Email
            </FloatChip>
          </Reveal>
        </div>
      </section>

      {/* ── 02 · DilaERP — copy split + full-width band ──────────────── */}
      <section
        aria-label="DilaERP"
        className="relative border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24"
      >
        <span
          aria-hidden="true"
          className="pv-ghost pointer-events-none absolute -top-6 left-0 select-none font-six-caps text-[20vw] leading-none"
        >
          02
        </span>
        <div className="mx-auto max-w-[1400px]">
          <div className="grid items-end gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-6">
              <ProductCopy product={erp} />
            </Reveal>
            <Reveal className="lg:col-span-6" delay={100}>
              <ul className="flex flex-col gap-5 border-l border-white/10 pl-8">
                <li className="text-lg font-light leading-snug text-white/75">
                  Fifteen modules from invoicing to payroll, behind one login.
                </li>
                <li className="text-lg font-light leading-snug text-white/75">
                  An AI assistant that answers in Kurdish, Arabic and English.
                </li>
                <li className="text-lg font-light leading-snug text-white/75">
                  Self-hosted. Your data stays yours.
                </li>
              </ul>
            </Reveal>
          </div>
          <Reveal className="relative mt-16" delay={150}>
            <AccentGlow accent={erp.accent} className="right-1/4 -top-10 h-72 w-72" />
            <Tilt>
              <BrowserShot
                src={erp.image}
                alt={erp.imageAlt}
                url={erp.domain}
                accent={erp.accent}
                aspect="aspect-[16/9]"
                sizes="(max-width: 1024px) 100vw, 85vw"
              />
            </Tilt>
            <FloatChip accent={erp.accent} className="left-10 top-10" dot>
              Profit &amp; Loss
            </FloatChip>
            <FloatChip accent={erp.accent} className="right-16 top-24" alt>
              Payroll
            </FloatChip>
            <FloatChip accent={erp.accent} className="bottom-12 right-1/3">
              POS · Stock · CRM
            </FloatChip>
          </Reveal>
        </div>
      </section>

      {/* ── 03 · dila.menu — phone fan over food ─────────────────────── */}
      <section
        aria-label="dila.menu"
        className="relative border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24"
      >
        <span
          aria-hidden="true"
          className="pv-ghost pointer-events-none absolute -top-6 right-0 select-none font-six-caps text-[20vw] leading-none"
        >
          03
        </span>
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <ProductCopy product={menu} />
          </Reveal>
          <Reveal className="relative lg:col-span-7" delay={120}>
            <AccentGlow accent={menu.accent} className="left-1/3 top-1/4 h-80 w-80" opacity={0.18} />
            <div className="relative mx-auto h-[540px] max-w-[560px] sm:h-[600px]">
              {/* Food backdrop card */}
              <div
                className="absolute inset-x-8 top-6 bottom-6 -rotate-2 overflow-hidden rounded-2xl border border-white/10 bg-cover bg-center opacity-45"
                style={{ backgroundImage: 'url(/images/products/dilamenu/food-koshky.webp)' }}
                aria-hidden="true"
              />
              <div className="absolute left-0 top-14 hidden w-[34%] -rotate-[8deg] sm:block">
                <PhoneShot
                  src="/images/products/dilamenu/welcome.webp"
                  alt="dila.menu welcome screen offering English, Arabic and Kurdish."
                  sizes="18vw"
                />
              </div>
              <div className="absolute left-1/2 top-0 z-10 w-[46%] -translate-x-1/2 sm:w-[42%]">
                <PhoneShot
                  src={menu.image}
                  alt={menu.imageAlt}
                  priority
                  sizes="(max-width: 640px) 46vw, 22vw"
                />
              </div>
              <div className="absolute right-0 top-20 hidden w-[32%] rotate-[8deg] sm:block">
                <PhoneShot
                  src="/images/products/dilamenu/item-modal.webp"
                  alt="dila.menu item detail showing weight, calories, origin and ingredients."
                  sizes="17vw"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Why ours is different ────────────────────────────────────── */}
      <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <SectionHead
              kicker="Why ours is different"
              title="Designed around local operations"
              lede="The same practical choices run through all three DilaCode products."
              accent={SITE_ACCENT}
            />
          </Reveal>
          <Reveal delay={120}>
            <FeatureRows
              accent={SITE_ACCENT}
              className="mt-14"
              rows={[
                {
                  title: 'Kurdish, Arabic and English from the start',
                  body: 'Interfaces, AI replies, receipts and PDFs support all three languages, including proper right-to-left layouts.',
                },
                {
                  title: 'We run what we sell',
                  body: 'We use DilaCRM in our own work, and restaurant teams use dila RMS in daily operations. Their feedback goes directly into the product.',
                },
                {
                  title: 'You keep your data',
                  body: 'Self-hosted deployment and data export options give each business control over its records.',
                },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ── Demo band ────────────────────────────────────────────────── */}
      <div className="px-6 md:px-12 lg:px-24">
        <div className="mx-auto max-w-[1400px]">
          <DemoBand
            ghost="products"
            title="See the products or book a demo"
            sub="Open a live product, or contact us for a guided walkthrough of the system that fits your business."
            primary={{ label: 'Book a demo', href: '/contact' }}
            accent={SITE_ACCENT}
            note="crm.dilaweb.com · app.datacode.app · dila.menu"
          />
        </div>
      </div>
    </div>
  );
}
